import { useEffect, useState } from "react";
import moment from "moment";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import { useAuth } from "../../provider/AuthProvider.tsx";
import usePolicyForm from "../../hooks/usePolicyForm.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import { revertNormalizeDate } from "../../utils/NormalizationUtil.ts";
import CommissionsGrid from "./CommissionsGrid.tsx";
import { Commission } from "../../types/insurance.ts";
import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import AgentsService from "../../services/AgentsService/AgentsService.ts";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const DEFAULT_OFFICE_VALUE = "1000";
const DEFAULT_AGENT_VALUE = "0";

const CommissionsSettings = () => {
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType | null>(null);
  const [allCommissions, setAllCommissions] = useState<Commission[]>([]);
  const [isOfficeMode, setIsOfficeMode] = useState(true);
  const [allAgentAndOfficesOptions, setAllAgentAndOfficesOptions] = useState<{
    [key: string]: string;
  }>({});

  const { jwt } = useAuth();
  const { baseInsuranceCompanies, policyTypes, offices, agentOptions } =
    usePolicyForm();

  const updatedBaseInsuranceCompanies = {
    "0": "Всички",
    ...baseInsuranceCompanies,
  };
  const updatedPolicyTypes = { "0": "Всички", ...policyTypes };
  const prefixedOffices = Object.entries(offices).reduce(
    (acc, [key, value]) => {
      if (typeof value === "string") acc[`999${key}`] = value;
      return acc;
    },
    {} as Record<string, string>
  );

  useEffect(() => {
    getDeltaOptions();
  }, [jwt]);

  const getDeltaOptions = async () => {
    try {
      const response = CapitalizationUtil.convertKeysToCamelCase(
        await AgentsService.getAgentsAndOffices(jwt)
      );
      if (!response) return;
      const { agents, officies } = response;
      const combinedMap: { [key: string]: string } = { all: "Всички" };
      Object.keys(officies).forEach((office: any) => {
        combinedMap[office] = officies[office];
      });
      Object.keys(agents).forEach((agent: any) => {
        combinedMap[agent] = agents[agent];
      });
      setAllAgentAndOfficesOptions(combinedMap);
    } catch (error) {
      displayAlert(
        "Грешка при зареждане на офиси и агенти",
        FadeAlertType.error
      );
    }
  };

  const [commissionsForm, setCommissionsForm] = useState({
    selectedAgentOrOfficeValue: "",
    insuranceCompany: "",
    percentage: "",
    foreignPercentage: "",
    startDate: moment().format("DD/MM/YYYY"),
    policyType: "",
  });

  const displayAlert = (message: string, type: FadeAlertType): void => {
    setAlert(message);
    setAlertType(type);
  };

  const handleToggleChange = () => {
    setIsOfficeMode(!isOfficeMode);
    setCommissionsForm((prev) => ({ ...prev, selectedAgentOrOfficeValue: "" }));
  };

  const fetchCommissionsData = async () => {
    const { selectedAgentOrOfficeValue, insuranceCompany, policyType } =
      commissionsForm;
    if (
      selectedAgentOrOfficeValue === DEFAULT_OFFICE_VALUE ||
      selectedAgentOrOfficeValue === DEFAULT_AGENT_VALUE
    ) {
      setAlert("Моля, изберете офис или агент!");
      setAlertType(FadeAlertType.error);
      return;
    }
    const fetchedCommissions: Commission[] =
      await InsuranceService.getCommissions(
        jwt,
        selectedAgentOrOfficeValue || "all",
        (policyType !== "0" && policyType) || "all",
        (insuranceCompany !== "0" && insuranceCompany) || "all"
      );
    const updatedCommissions = fetchedCommissions.map((commission) => ({
      ...commission,
      selectedAgentOrOfficeValue:
        allAgentAndOfficesOptions[commission.agentId ? commission.agentId : ""],
    }));
    setAllCommissions(updatedCommissions);
  };

  const saveCommissionsData = async () => {
    if (!validateData()) return;
    const {
      selectedAgentOrOfficeValue,
      policyType,
      insuranceCompany,
      percentage,
      startDate,
      foreignPercentage,
    } = commissionsForm;

    try {
      const response = await InsuranceService.saveCommissions(
        jwt,
        selectedAgentOrOfficeValue,
        policyType,
        insuranceCompany,
        percentage,
        revertNormalizeDate(startDate),
        foreignPercentage
      );
      if (response)
        displayAlert("Успешен запис на комисион", FadeAlertType.success);
    } catch (error) {
      displayAlert("Грешка при запис на комисион", FadeAlertType.error);
    }
  };

  const validateData = () => {
    const {
      selectedAgentOrOfficeValue,
      policyType,
      insuranceCompany,
      percentage,
      startDate,
    } = commissionsForm;
    if (!selectedAgentOrOfficeValue) {
      displayAlert("Моля, изберете офис или агент.", FadeAlertType.error);
      return false;
    }
    if (!policyType || policyType === "0") {
      displayAlert("Моля, изберете вид полица.", FadeAlertType.error);
      return false;
    }
    if (!insuranceCompany || policyType === "0") {
      displayAlert(
        "Моля, изберете застрахователна компания.",
        FadeAlertType.error
      );
      return false;
    }
    if (!percentage) {
      displayAlert("Моля, въведете процент.", FadeAlertType.error);
      return false;
    }
    if (!startDate) {
      displayAlert("Моля, изберете начална дата.", FadeAlertType.error);
      return false;
    }
    return true;
  };

  return (
    <>
      <Breadcrumb pageName="Комисионни" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Комисионни" />

        <div className="p-3">
          {/* Row 1 - 3 filters */}
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-3">
            <div>
              <label className={labelClass}>Застрахователна Компания</label>
              <SelectGroup
                options={updatedBaseInsuranceCompanies}
                value={commissionsForm.insuranceCompany}
                onChange={(val) =>
                  setCommissionsForm((prev) => ({
                    ...prev,
                    insuranceCompany: val,
                  }))
                }
                name="insuranceCompany"
              />
            </div>
            <div>
              <label className={labelClass}>Вид полица</label>
              <SelectGroup
                options={updatedPolicyTypes}
                value={commissionsForm.policyType}
                onChange={(val) =>
                  setCommissionsForm((prev) => ({ ...prev, policyType: val }))
                }
                name="policyType"
              />
            </div>
            <div>
              <div className="mb-1 flex items-center gap-2">
                <label className="text-xs font-medium text-black dark:text-white">
                  Офиси
                </label>
                <button
                  type="button"
                  onClick={handleToggleChange}
                  className="focus:ring-primary relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
                  style={{ backgroundColor: "#5A1400" }}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      isOfficeMode ? "translate-x-1" : "translate-x-5"
                    }`}
                  />
                </button>
                <label className="text-xs font-medium text-black dark:text-white">
                  Агенти
                </label>
              </div>
              <SelectGroup
                options={isOfficeMode ? prefixedOffices : agentOptions}
                value={commissionsForm.selectedAgentOrOfficeValue}
                shouldSort={false}
                onChange={(val) =>
                  setCommissionsForm((prev) => ({
                    ...prev,
                    selectedAgentOrOfficeValue: val,
                  }))
                }
                name={isOfficeMode ? "office" : "agent"}
              />
            </div>
          </div>

          {/* Row 2 - commission inputs + date + buttons */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div>
              <label className={labelClass}>Комисион</label>
              <input
                type="text"
                className={inputClass}
                onChange={(e) =>
                  setCommissionsForm((prev) => ({
                    ...prev,
                    percentage: e.target.value,
                  }))
                }
                value={commissionsForm.percentage}
              />
            </div>
            <div>
              <label className={labelClass}>Комисион (чужди)</label>
              <input
                type="text"
                className={inputClass}
                onChange={(e) =>
                  setCommissionsForm((prev) => ({
                    ...prev,
                    foreignPercentage: e.target.value,
                  }))
                }
                value={commissionsForm.foreignPercentage}
                placeholder="0"
              />
            </div>
            <div>
              <DatePicker
                label="Действа от дата"
                onChange={(event) =>
                  setCommissionsForm((prev) => ({
                    ...prev,
                    startDate: event.target.value,
                  }))
                }
                value={commissionsForm.startDate}
                name="startDate"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchCommissionsData}
                className="bg-primary w-full rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Справка
              </button>
            </div>
            <div className="flex items-end">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  saveCommissionsData();
                }}
                className="bg-primary w-full rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Добави
              </button>
            </div>
          </div>
        </div>
      </div>
      <CommissionsGrid commissions={allCommissions} isLoading={false} />
    </>
  );
};

export default CommissionsSettings;
